import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatHM, parseEntryLength } from '../../utils/timesheet';
import { usePanelPosition, useRevealShowing } from './clockPanel';
import './ClockPicker.css';

/**
 * A length of time in seconds (null when empty), shown and edited as hours and minutes, each on
 * its own, so only a real length can be entered. Entering the field opens a panel of hours and
 * minutes to click, the minutes every `step` (the logging interval). Or type: any minutes (90 in
 * the minutes becomes 1h 30m), a decimal in the hours (1.5), or paste something like "1h 30m". The
 * arrow keys step the hours by 1 and the minutes by `step`. Up to 24 hours.
 */

const HOURS = Array.from({ length: 25 }, (_, hour) => hour);
const DEFAULT_STEP = 15;
const PANEL_WIDTH = 190;
const PANEL_HEIGHT = 300;
const MAX_MINUTES = 24 * 60;

export default function LengthPicker({
  value,
  onChange,
  step: stepProp = DEFAULT_STEP,
  inputRef = null,
  onClose = null,
  invalid = false,
  disabled = false,
  'aria-label': ariaLabel = 'Length',
}) {
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [part, setPart] = useState(null);
  // The hours as typed while they're a decimal on the way to something: "1." then "1.5".
  const [decimal, setDecimal] = useState(null);

  const empty = value === null || value === undefined;
  const total = empty ? 0 : Math.round(value / 60);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;

  const put = (nextMinutes) => onChange(Math.max(0, Math.min(MAX_MINUTES, nextMinutes)) * 60);

  const step = stepProp || DEFAULT_STEP;
  const minuteOptions = Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);
  const pos = usePanelPosition(open, wrapRef, PANEL_WIDTH, PANEL_HEIGHT);
  const minuteShown = Math.floor(minutes / step) * step;
  useRevealShowing(panelRef, Boolean(open && pos), [hours, minuteShown, step]);

  const close = () => {
    if (!open) return;
    setOpen(false);
    setPart(null);
    setDecimal(null);
    onClose?.();
  };

  const enter = (which) => (e) => {
    setPart(which);
    setOpen(true);
    e.target.select();
  };

  const typeHours = (e) => {
    const text = e.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
    if (text.includes('.')) {
      setDecimal(text);
      const parsed = Number(text);
      if (Number.isFinite(parsed) && parsed <= 24) put(Math.round(parsed * 60));
      return;
    }
    setDecimal(null);
    if (!text) {
      if (minutes) put(minutes);
      else onChange(null);
      return;
    }
    let next = Number(text.slice(-2));
    if (next > 24) next = Number(text.slice(-1));
    put(next * 60 + minutes);
  };

  const typeMinutes = (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    setDecimal(null);
    if (!digits) {
      if (hours) put(hours * 60);
      else onChange(null);
      return;
    }
    put(hours * 60 + Number(digits.slice(-2)));
  };

  const paste = (e) => {
    const seconds = parseEntryLength(e.clipboardData.getData('text'));
    if (seconds === null) return;
    e.preventDefault();
    onChange(seconds);
  };

  // Enter closes an open panel rather than submitting the form around it.
  // Escape likewise only closes the panel, not a floating form the field sits in.
  const closingKeys = (e) => {
    if (e.key !== 'Escape' && e.key !== 'Enter') return false;
    if (open) {
      e.preventDefault();
      e.stopPropagation();
    }
    close();
    return true;
  };

  const onBlur = (e) => {
    const next = e.relatedTarget;
    if (next && (wrapRef.current?.contains(next) || panelRef.current?.contains(next))) return;
    close();
  };

  const finish = () => {
    const focused = document.activeElement;
    if (wrapRef.current?.contains(focused)) focused.blur();
    else close();
  };

  // Panel buttons never take focus, so the field keeps it (and its keys).
  const hold = (fn) => (e) => {
    e.preventDefault();
    fn();
  };

  const seg = (which) => `ts-clock__seg${open && part === which ? ' is-active' : ''}`;

  const panel = open && pos ? createPortal(
    <div
      ref={panelRef}
      className="ts-clock-panel ts-length-panel"
      role="dialog"
      aria-label={ariaLabel}
      style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: PANEL_WIDTH }}
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="ts-clock-panel__head">
        <span className="ts-clock-panel__preview">{formatHM(total * 60)}</span>
      </div>
      <div className="ts-clock-panel__cols">
        <div className="ts-clock-panel__col" role="listbox" aria-label="Hours">
          {HOURS.map((hour) => {
            const on = !empty && hour === hours;
            return (
              <button
                key={hour}
                type="button"
                tabIndex={-1}
                role="option"
                aria-selected={on}
                data-showing={hour === hours}
                className={`ts-clock-panel__opt${on ? ' is-on' : ''}`}
                onMouseDown={hold(() => { setDecimal(null); put(hour * 60 + minutes); setPart('hours'); })}
              >
                {hour}h
              </button>
            );
          })}
        </div>
        <div className="ts-clock-panel__side">
          <div className="ts-clock-panel__col" role="listbox" aria-label="Minutes">
            {minuteOptions.map((minute) => {
              const on = !empty && minute === minutes;
              return (
                <button
                  key={minute}
                  type="button"
                  tabIndex={-1}
                  role="option"
                  aria-selected={on}
                  data-showing={minute === minuteShown}
                  className={`ts-clock-panel__opt${minute % 15 === 0 ? ' is-quarter' : ''}${on ? ' is-on' : ''}`}
                  onMouseDown={hold(() => { setDecimal(null); put(hours * 60 + minute); setPart('minutes'); })}
                >
                  {String(minute).padStart(2, '0')}m
                </button>
              );
            })}
          </div>
          <button type="button" tabIndex={-1} className="bdc-btn bdc-btn--primary bdc-btn--sm ts-clock-panel__ok" onClick={finish}>
            OK
          </button>
        </div>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div
      ref={wrapRef}
      className={`ts-clock ts-clock--sm ts-length${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${invalid ? ' is-invalid' : ''}`}
      role="group"
      aria-label={ariaLabel}
      onBlur={onBlur}
      onMouseDown={(e) => {
        // A click on anything that isn't one of the two boxes goes to the hours.
        if (e.target.tagName !== 'INPUT' && !disabled) {
          e.preventDefault();
          hourRef.current?.focus();
        }
      }}
    >
      <input
        ref={(el) => {
          hourRef.current = el;
          if (inputRef) inputRef.current = el;
        }}
        className={`${seg('hours')} ts-clock__seg--hour`}
        type="text"
        inputMode="decimal"
        aria-label={`${ariaLabel}, hours`}
        placeholder="0"
        value={decimal ?? (empty ? '' : String(hours))}
        onFocus={enter('hours')}
        onChange={typeHours}
        onPaste={paste}
        onKeyDown={(e) => {
          if (closingKeys(e)) return;
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            setDecimal(null);
            put(total + (e.key === 'ArrowUp' ? 60 : -60));
          } else if (e.key === 'ArrowRight' || e.key === ':' || e.key.toLowerCase() === 'h') {
            e.preventDefault();
            minuteRef.current?.focus();
          }
        }}
        disabled={disabled}
      />
      <span className="ts-length__unit" aria-hidden="true">h</span>
      <input
        ref={minuteRef}
        className={seg('minutes')}
        type="text"
        inputMode="numeric"
        aria-label={`${ariaLabel}, minutes`}
        placeholder="00"
        value={empty ? '' : String(minutes).padStart(2, '0')}
        onFocus={enter('minutes')}
        onChange={typeMinutes}
        onPaste={paste}
        onKeyDown={(e) => {
          if (closingKeys(e)) return;
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            put(total + (e.key === 'ArrowUp' ? step : -step));
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            hourRef.current?.focus();
          }
        }}
        disabled={disabled}
      />
      <span className="ts-length__unit" aria-hidden="true">m</span>
      <i className="bi bi-chevron-down ts-clock__icon" aria-hidden="true" />
      {panel}
    </div>
  );
}
